import Utils from './utils'
import CONST from './constants'
import _ from 'lodash'

class FiltersByGroup {
  constructor (mediaTagFilter) {
    this.mediaTagFilters = mediaTagFilter
  }

  findLedTags (addPrefixLED = false) {
    return _.chain(this.findByGroupKey(CONST.FILTER_KEY_LED_TAGS))
      .map(item => {
        if (addPrefixLED) {
          item = `LED:${item}`
        }

        return item
      })
      .value()
  }

  findVideowallTags (addPrefixLED = false) {
    return _.chain(this.findByGroupKey(CONST.FILTER_KEY_VIDEOWALL_TAGS))
      .map(item => {
        if (addPrefixLED) {
          item = `LED:${item}`
        }

        return item
      })
      .value()
  }

  findMediaTags () {
    return this.findByGroupKey(CONST.FILTER_KEY_MEDIA_TAGS)
  }

  findMediaTypes () {
    // Normalize both URL formats into individual elements:
    //   repeated-keys: mediaTypes=img&mediaTypes=video - findByGroupKey returns ['img', 'video']
    //   comma-joined:  mediaTypes=img,video - findByGroupKey returns ['img,video']
    // The comma-joined format can appear in manually typed URLs, old bookmarks, or
    // when the residuals-preservation loop re-uses a previously stored comma-joined value.
    return this.findByGroupKey(CONST.FILTER_KEY_MEDIA_TYPES)
      .flatMap(v => v.split(','))
      .filter(Boolean)
  }

  findPlayerTags () {
    return this.findByGroupKey(CONST.FILTER_KEY_PLAYER_TAGS)
  }

  findPopIds () {
    return this.findByGroupKey(CONST.FILTER_KEY_POPIDS)
  }

  findSearchText () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_SEARCH_TEXT)

    if (!_.isEmpty(results)) {
      if (_.isArray(results)) {
        return results[0]
      }

      return results
    }

    return ''
  }

  findOrientation () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_ORIENTATION)

    if (!_.isEmpty(results)) {
      if (_.isArray(results)) {
        return results[0]
      }

      return results
    }

    return null
  }

  /**
   * Returns the persisted scheduled filter as a boolean or null.   *
   * @returns {boolean | null}
   */
  findScheduled () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_SCHEDULED)
    if (!_.isEmpty(results)) {
      const raw = _.isArray(results) ? results[0] : results
      if (raw === 'true') return true
      if (raw === 'false') return false
    }
    return null
  }

  /**
   * Returns the persisted modifiedAtFrom value as a string
   * with DateTime ISODate format or null.
   * Note: To be converted to ISO 8601 string before sending
   * to API.
   * @returns {string | null}
   */
  findModifiedAtFrom () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_MODIFIED_AT_FROM)
    if (!_.isEmpty(results)) {
      const raw = _.isArray(results) ? results[0] : results
      return raw || null
    }
    return null
  }

  /**
   * Returns the persisted modifiedAtTo value as a string
   * with DateTime ISODate format or null.
   * Note: To be converted to ISO 8601 string before sending
   * to API.
   * @returns {string | null}
   */
  findModifiedAtTo () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_MODIFIED_AT_TO)
    if (!_.isEmpty(results)) {
      const raw = _.isArray(results) ? results[0] : results
      return raw || null
    }
    return null
  }

  /**
   * Returns the persisted sortOrder token or null when absent (API default applies).
   * Uses the format `field:direction` (e.g. `desc`).
   * @returns {string | null}
   */
  findSortOrder () {
    const results = this.findByGroupKey(CONST.FILTER_KEY_SORT_ORDER)
    if (!_.isEmpty(results)) {
      const raw = _.isArray(results) ? results[0] : results
      return raw || null
    }
    return null
  }

  saveLedTags (data = [], isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_LED_TAGS, isUpdateLocation)
  }

  saveVideowallTags (data = [], isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_VIDEOWALL_TAGS, isUpdateLocation)
  }

  saveMediaTags (data, isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_MEDIA_TAGS, isUpdateLocation)
  }

  savePlayerTags (data, isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_PLAYER_TAGS, isUpdateLocation)
  }

  saveMediaTypes (data, isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_MEDIA_TYPES, isUpdateLocation)
  }

  savePopIds (data, isUpdateLocation = true) {
    this.save(data, CONST.FILTER_KEY_POPIDS, isUpdateLocation)
  }

  saveSearchText (data, isUpdateLocation = true) {
    this.save([data], CONST.FILTER_KEY_SEARCH_TEXT, isUpdateLocation)
  }

  saveOrientation (data, isUpdateLocation = true) {
    // Wrap in an array so updateWindowLocationQueryParams's `_.isArray(q.values)`
    // guard passes and the key is actually written to the URL.
    // findOrientation() already handles the array form via its _.isArray() branch.
    this.save([data], CONST.FILTER_KEY_ORIENTATION, isUpdateLocation)
  }

  /**
   * Persists the scheduled filter as its own URL key (`scheduled=true|false`).
   * @param {boolean} value
   * @param {boolean} [isUpdateLocation=true]
   */
  saveScheduled (value, isUpdateLocation = true) {
    this.save([String(value)], CONST.FILTER_KEY_SCHEDULED, isUpdateLocation)
  }

  /**
   * Persists the modifiedAtFrom value as a string with
   * DateTime ISODate format.
   * Note: To be converted to ISO 8601 string before sending
   * to API.
   * @param {string} value
   * @param {boolean} [isUpdateLocation=true]
   */
  saveModifiedAtFrom (value, isUpdateLocation = true) {
    this.save([value], CONST.FILTER_KEY_MODIFIED_AT_FROM, isUpdateLocation)
  }

  /** @param {string} value — ISO 8601 date string */
  /**
   * Persists the modifiedAtTo value as a string with
   * DateTime ISODate format.
   * Note: To be converted to ISO 8601 string before sending
   * to API.
   * @param {string} value
   * @param {boolean} [isUpdateLocation=true]
   */
  saveModifiedAtTo (value, isUpdateLocation = true) {
    this.save([value], CONST.FILTER_KEY_MODIFIED_AT_TO, isUpdateLocation)
  }

  /**
   * Persists the sortOrder token. Call `deleteSortOrder()` when the value equals
   * `CONST.SORT_ORDER_DEFAULT` to keep the URL clean.
   * @param {string} value
   * @param {boolean} [isUpdateLocation=true]
   */
  saveSortOrder (value, isUpdateLocation = true) {
    this.save([value], CONST.FILTER_KEY_SORT_ORDER, isUpdateLocation)
  }

  deleteLedTags () {
    return this.delete(CONST.FILTER_KEY_LED_TAGS)
  }

  deleteVideowallTags () {
    return this.delete(CONST.FILTER_KEY_VIDEOWALL_TAGS)
  }

  deleteMediaTags () {
    return this.delete(CONST.FILTER_KEY_MEDIA_TAGS)
  }

  deletePlayerTags (data) {
    return this.delete(CONST.FILTER_KEY_PLAYER_TAGS)
  }

  deleteMediaTypes () {
    return this.delete(CONST.FILTER_KEY_MEDIA_TYPES)
  }

  deletePopIds () {
    return this.delete(CONST.FILTER_KEY_POPIDS)
  }

  deleteSearchText () {
    return this.delete(CONST.FILTER_KEY_SEARCH_TEXT)
  }

  deleteOrientation () {
    return this.delete(CONST.FILTER_KEY_ORIENTATION)
  }

  /**
   * Deletes the scheduled filter. When `isUpdateLocation`
   * is true, also removes the `scheduled` key from the URL.
   * @param {boolean} [isUpdateLocation=true]
   */
  deleteScheduled (isUpdateLocation = true) {
    if (isUpdateLocation) {
      return this.delete(CONST.FILTER_KEY_SCHEDULED)
    }
    this.filters = this.filters.filter(item => item.key !== CONST.FILTER_KEY_SCHEDULED)
  }

  /**
   * Deletes the modifiedAtFrom filter, also removes the
   * `modifiedAtFrom` key from the URL.
   */
  deleteModifiedAtFrom () {
    this.delete(CONST.FILTER_KEY_MODIFIED_AT_FROM)
  }

  /**
   * Deletes the modifiedAtTo filter, also removes the
   * `modifiedAtTo` key from the URL.
   */
  deleteModifiedAtTo () {
    this.delete(CONST.FILTER_KEY_MODIFIED_AT_TO)
  }

  /**
   * Deletes the sortOrder filter, also removes the `sortOrder`
   * key from the URL.
   */
  deleteSortOrder () {
    this.delete(CONST.FILTER_KEY_SORT_ORDER)
  }
}

class FiltersRepository extends FiltersByGroup {
  constructor () {
    super()
    this.filters = []
  }

  findAll () {
    return this.filters
  }

  findByGroupKey (groupKey) {
    const getGroup = this.filters.filter(item => item.key === groupKey)

    if (getGroup.length === 1) {
      return getGroup[0].values
    }

    return []
  }

  save (data, groupKey, isUpdateLocation = false) {
    const getTargetPosition = this.filters.findIndex(item => item.key === groupKey)

    if (getTargetPosition >= 0) {
      this.filters[getTargetPosition].values = data
    } else {
      this.filters.push({
        key: groupKey,
        values: data
      })
    }

    if (isUpdateLocation) {
      Utils.updateWindowLocationQueryParams(this.filters)
    }
  }

  delete (groupKey) {
    this.filters = this.filters.filter(item => item.key !== groupKey)
    // Include the deleted key with an empty values array so that
    // updateWindowLocationQueryParams's residuals-preservation loop sees the key
    // as "already accounted for" and does not re-add the old value from the
    // current URL. encodeQueryString skips empty arrays, and _.pickBy then
    // drops the empty entry, so the key is fully removed from the new URL.
    Utils.updateWindowLocationQueryParams([
      ...this.filters,
      { key: groupKey, values: [] }
    ])
  }

  /**
   * Removes multiple filter keys in a single URL update.
   *
   * When deleting keys one-by-one in the same synchronous tick, each `delete()`
   * call reads from `window.location` via `getURL()`. Because `router.push` is
   * async, the URL hasn't been updated yet by the time the next call fires, so
   * the residuals-preservation loop in `updateWindowLocationQueryParams` re-adds
   * keys that were already removed. This method avoids that race by batching all
   * removals into a single `updateWindowLocationQueryParams` call.
   *
   * @param {string[]} groupKeys
   */
  deleteKeys (groupKeys) {
    this.filters = this.filters.filter(item => !groupKeys.includes(item.key))
    Utils.updateWindowLocationQueryParams([
      ...this.filters,
      ...groupKeys.map(key => ({ key, values: [] }))
    ])
  }

  /**
   * Saves or deletes multiple filter keys in a single URL update.
   *
   * Use this instead of calling individual `save()`/`delete()` methods in the
   * same synchronous tick. Each individual call triggers its own `router.push`
   * (async), so the URL hasn't changed by the time the next call reads it —
   * causing the residuals-preservation loop to re-add keys that were just removed.
   *
   * @param {Array<{ key: string, values: string[] | null }>} entries
   *   `values: null` or `[]` → delete the key; non-empty array → save the key.
   */
  setKeys (entries) {
    const keysToDelete = []

    for (const { key, values } of entries) {
      if (!values || values.length === 0) {
        this.filters = this.filters.filter(item => item.key !== key)
        keysToDelete.push(key)
      } else {
        const pos = this.filters.findIndex(item => item.key === key)
        if (pos >= 0) {
          this.filters[pos].values = values
        } else {
          this.filters.push({ key, values })
        }
      }
    }

    // Deleted keys must be explicitly passed with empty values so the residuals-
    // preservation loop in updateWindowLocationQueryParams doesn’t re-add them
    // from the current (not yet updated) URL.
    Utils.updateWindowLocationQueryParams([
      ...this.filters,
      ...keysToDelete.map(key => ({ key, values: [] }))
    ])
  }

  deleteAll (isUpdateLocation = false) {
    // Capture all currently-tracked filter keys before clearing the list.
    // We pass them with empty values so updateWindowLocationQueryParams
    // explicitly removes them from the URL instead of re-adding them via
    // its residuals-preservation loop.
    const toExplicitlyClear = this.filters.map(f => ({ key: f.key, values: [] }))
    this.filters = []

    if (isUpdateLocation) {
      Utils.updateWindowLocationQueryParams(toExplicitlyClear)
    }
  }

  // Get old filters from localStorage and save it to filtersRepository
  mergeMediaPageFiltersFromLocalStorage () {
    if (!['/media', '/led', '/alert'].includes(window.location.pathname)) return

    _.delay(() => {
      const selectedTagsFromSlider = _.isObject(this.$dataStore.getStore().selectedTagsFromSlider) ? this.$dataStore.getStore().selectedTagsFromSlider : {}
      const selectedPopIdsFromSlider = _.isObject(this.$dataStore.getStore().selectedPopIdsFromSlider) ? this.$dataStore.getStore().selectedPopIdsFromSlider : {}
      const selectedTags = _.isArray(selectedTagsFromSlider.mediaFiles) ? selectedTagsFromSlider.mediaFiles : []
      const selectedPopids = _.isArray(selectedPopIdsFromSlider.mediaFiles) ? selectedPopIdsFromSlider.mediaFiles : []

      if (!_.isEmpty(selectedTags) || !_.isEmpty(selectedPopids)) {
        this.saveMediaTags(_.uniq(this.findMediaTags().concat(selectedTags)))
        this.savePopIds(_.uniq(this.findPopIds().concat(selectedPopids)))
      }

      // Remove tag and popId filters from localStorage that is related to files page, alert page and LED Page
      for (const key of ['mediaFiles', 'ledPageMediaTags', 'ledPageResolutionTags', 'mediaAlert', 'mediaLED']) {
        delete selectedTagsFromSlider[key]
        delete selectedPopIdsFromSlider[key]
        this.$dataStore.setStore({ selectedTagsFromSlider, selectedPopIdsFromSlider })
      }
    }, 10)
  }

  /**
   * Initialize with DataStore dependency
   *
   * @param {any} dataStoreInstance
   */
  init (dataStoreInstance) {
    const filterKeys = [
      CONST.FILTER_KEY_LED_TAGS,
      CONST.FILTER_KEY_VIDEOWALL_TAGS,
      CONST.FILTER_KEY_MEDIA_TAGS,
      CONST.FILTER_KEY_PLAYER_TAGS,
      CONST.FILTER_KEY_MEDIA_TYPES, // @deprecated — 'scheduled' used to be embedded here; read by initialize() for legacy URL compat
      CONST.FILTER_KEY_ORIENTATION,
      CONST.FILTER_KEY_POPIDS,
      CONST.FILTER_KEY_SEARCH_TEXT,
      CONST.FILTER_KEY_SCHEDULED,
      CONST.FILTER_KEY_MODIFIED_AT_FROM,
      CONST.FILTER_KEY_MODIFIED_AT_TO,
      CONST.FILTER_KEY_SORT_ORDER
    ]
    const queryParams = Utils.getWindowLocationQueryParams()
    const filters = []

    // Keys whose values must be treated as individual items even when the URL
    // uses comma-joined format instead of repeated keys (e.g. mediaTypes=img,video
    // instead of mediaTypes=img&mediaTypes=video). Vue Router does not
    // percent-encode commas in query values, so a comma-joined value arrives as a
    // single string element. Normalizing here ensures this.filters is always in
    // the canonical repeated-key form so subsequent updateWindowLocationQueryParams
    // calls re-emit the correct format and the URL self-heals on next interaction.
    const COMMA_JOINED_KEYS = new Set([CONST.FILTER_KEY_MEDIA_TYPES])

    for (const filterKey of filterKeys) {
      const queryProps = Object.keys(queryParams)

      if (queryProps.includes(filterKey)) {
        const queryValue = queryParams[filterKey]

        filters.push({
          key: filterKey,
          values: COMMA_JOINED_KEYS.has(filterKey)
            ? queryValue.flatMap(v => v.split(',')).filter(Boolean)
            : queryValue
        })
      }
    }

    this.filters = filters
    this.$dataStore = dataStoreInstance
    this.mergeMediaPageFiltersFromLocalStorage()
  }
}

export default new FiltersRepository()
